import { useState, useCallback, useEffect, useRef } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { FilterBar } from '../../components/orders/FilterBar';
import { OrderCard } from '../../components/orders/OrderCard';
import { EmptyState } from '../../components/orders/EmptyState';
import { Pagination } from '../../components/orders/Pagination';
import { CancelOrderModal } from '../../components/orders/CancelOrderModal';
import { useOrders } from '../../hooks/useOrders';
import { useOrderRealtime } from '../../hooks/useOrderRealtime';
import { getCSRFToken } from '../../utils/csrf';
import type { Order, OrdersResponse, FilterState, OrderRoutes } from '../../types/orders';

interface OrdersProps {
  orders: OrdersResponse;
  filters: FilterState;
  routes: OrderRoutes;
  pusherKey: string;
  pusherCluster: string;
}

const PENDING_CANCELATION = 5;
const POLL_INTERVAL_MS = 3000;

export default function Orders({ orders: initialOrders, filters, routes, pusherKey, pusherCluster }: OrdersProps) {
  const { orders, updateOrderStatus, updateOrder } = useOrders(initialOrders);
  const [cancelModalOrder, setCancelModalOrder] = useState<Order | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const pollingRef = useRef<Map<number, ReturnType<typeof setInterval>>>(new Map());

  // Real-time updates for orders pending cancellation
  const pendingCancelationIds = orders.data
    .filter(order => order.status === PENDING_CANCELATION)
    .map(order => order.id);

  useOrderRealtime(pendingCancelationIds, (orderId, newStatus) => {
    updateOrder(orderId, { status: 4, status_name: newStatus, is_user_cancellable: false });
    // Stop polling if real-time update arrives
    const interval = pollingRef.current.get(orderId);
    if (interval) {
      clearInterval(interval);
      pollingRef.current.delete(orderId);
    }
  }, { key: pusherKey, cluster: pusherCluster });

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      pollingRef.current.forEach(interval => clearInterval(interval));
      pollingRef.current.clear();
    };
  }, []);

  const pollOrderStatus = useCallback((orderId: number) => {
    const statusUrl = routes.order_status.replace('__ID__', String(orderId));
    const interval = setInterval(async () => {
      try {
        const res = await fetch(statusUrl, {
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.status !== PENDING_CANCELATION) {
          updateOrder(orderId, {
            status: data.status,
            status_name: data.status_name,
            is_user_cancellable: false,
          });
          clearInterval(interval);
          pollingRef.current.delete(orderId);
        }
      } catch {
        // silently retry on next interval
      }
    }, POLL_INTERVAL_MS);
    pollingRef.current.set(orderId, interval);
  }, [routes.order_status, updateOrder]);

  const handleFilter = useCallback((newFilters: FilterState) => {
    const params = new URLSearchParams();
    if (newFilters.date_from) params.set('date_from', newFilters.date_from);
    if (newFilters.date_to) params.set('date_to', newFilters.date_to);
    const qs = params.toString();
    window.location.href = routes.index + (qs ? '?' + qs : '');
  }, [routes.index]);

  const handleClearFilter = useCallback(() => {
    window.location.href = routes.index;
  }, [routes.index]);

  const handlePageChange = useCallback((page: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', String(page));
    window.location.href = routes.index + '?' + params.toString();
  }, [routes.index]);

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelModalOrder) return;

    setCancelLoading(true);
    try {
      const formData = new FormData();
      formData.append('id', String(cancelModalOrder.id));

      const response = await fetch(routes.cancel, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-XSRF-TOKEN': getCSRFToken(),
          'Accept': 'text/html,application/xhtml+xml',
        },
        body: formData,
      });

      if (response.ok || response.redirected) {
        updateOrder(cancelModalOrder.id, {
          status: PENDING_CANCELATION,
          status_name: 'En proceso de cancelación',
          is_user_cancellable: false,
        });
        pollOrderStatus(cancelModalOrder.id);
        setCancelModalOrder(null);
      } else {
        throw new Error('Error al cancelar el pedido');
      }
    } catch {
      alert('Error al cancelar el pedido. Por favor intentá de nuevo.');
    } finally {
      setCancelLoading(false);
    }
  }, [cancelModalOrder, routes.cancel, updateOrder, pollOrderStatus]);

  const hasOrders = orders.data.length > 0;
  const hasFilters = !!(filters.date_from || filters.date_to);
  const totalPages = orders.last_page;

  return (
    <MainLayout>
      <Head>
        <title>Mis Compras</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-6">
        <PageHeader title="Mis Compras" subtitle="Revisá el historial de tus pedidos y su estado." />

        {/* No orders at all, no filters active */}
        {!hasOrders && !hasFilters && (
          <EmptyState variant="no-orders" productsRoute={routes.products} />
        )}

        {/* Show filter + content when there are orders or active filters */}
        {(hasOrders || hasFilters) && (
          <>
            <div className="mb-6">
              <FilterBar filters={filters} onFilter={handleFilter} onClear={handleClearFilter} />
            </div>

            {hasOrders ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
                  {orders.data.map(order => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      routes={routes}
                      onCancel={setCancelModalOrder}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <Pagination
                    currentPage={orders.current_page}
                    lastPage={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            ) : (
              <EmptyState variant="no-results" productsRoute={routes.products} />
            )}
          </>
        )}

        <CancelOrderModal
          isOpen={cancelModalOrder !== null}
          orderNumber={cancelModalOrder?.id || 0}
          onConfirm={handleCancelConfirm}
          onCancel={() => !cancelLoading && setCancelModalOrder(null)}
          loading={cancelLoading}
        />
      </div>
    </MainLayout>
  );
}
